import React from 'react'

import { Select, SelectItem, SelectOption, SelectPlaceholder } from '../../../../components/select'
import { SelectListProps } from '../../../../components/select/select-list'
import { TRANSACTION_LABEL } from '../../constants/general'
import { getSymbolType, getTransactionsForSymbolType } from '../../utils'
import { usePortfolioBulkEditRowContext } from '../PortfolioBulkEditRowContext'
import { EditColumnProps } from './types'

interface TransactionEditColumnProps<StateType, ErrorsType> extends EditColumnProps<StateType, ErrorsType> {
  transactions?: Array<SelectItem<number>>
  optionListProps?: Omit<SelectListProps, 'items' | 'selectedIndex' | 'onChange' | 'state'>
}

export function TransactionEditColumn<
  StateType extends {
    ticker: string
    transaction?: number
  },
  ErrorsType extends {
    transaction?: boolean
  },
>({
  transactions,
  optionListProps = { className: 'min-w-28' },
  ...props
}: TransactionEditColumnProps<StateType, ErrorsType>) {
  const { isActive, setIsInteracting } = usePortfolioBulkEditRowContext()
  const transactionOptions = React.useMemo<SelectItem<number>[]>(() => {
    if (transactions?.length) return transactions
    const symbolType = getSymbolType(props.entry.ticker)
    const options = getTransactionsForSymbolType(symbolType)

    return options.map((value) => ({ label: TRANSACTION_LABEL[value], value }))
  }, [props.entry.ticker, transactions])

  return (
    <>
      <input form={props.form} type="hidden" value={props.entry.transaction} />
      {isActive ? (
        <Select
          aria-label="Transaction"
          className={props.className}
          label={props.label}
          isHorizontalLabel={props.isHorizontalLabel}
          labelClassName={props.labelClassName}
          value={props.entry.transaction}
          items={transactionOptions}
          onChange={(item) => props.setState((entry) => ({ ...entry, transaction: item.value }))}
          listProps={optionListProps}
          size={props.size}
          data-testid="transaction-select"
          onIsInteractingChange={setIsInteracting}
        >
          {transactionOptions.map((item) => (
            <SelectOption key={item.value} data-testid={`transaction-option-${item.value}`}>
              {item.label}
            </SelectOption>
          ))}
        </Select>
      ) : (
        <SelectPlaceholder
          className={props.className}
          size={props.size}
          buttonContent={
            transactionOptions.find(({ value }) => value === props.entry.transaction)?.label ??
            transactionOptions[0].label
          }
        />
      )}
    </>
  )
}
